import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";

/**
 * ControlCluster — estilo GLOBAL del trío de botones de control (recargar ·
 * expandir · cerrar y similares) que corona popups, visores y menús de SmartDx.
 *
 * Diseño elegido (mockup "Opción C — toolbar segmentada"): los íconos viven
 * dentro de UNA sola píldora glass con divisores finos, en vez de 3 círculos
 * sueltos. Se lee como una barra de herramientas del visor, no como acciones
 * aisladas, y escala si se agregan más (descargar, imprimir).
 *
 * Colores 100% por tokens de theme.js (theme-aware light/dark). El glass se
 * compone con --fg-rgb (foreground: blanco en dark, oscuro en light) para que
 * el tint sea sutil en ambos modos — NO usar --surface-rgb, que en dark es
 * negro. Ref: mockup tools/mockups/smartdx_mockups_control_cluster_buttons.html.
 *
 * Uso:
 *   <ControlCluster
 *     actions={[
 *       { key: "reload", icon: <RefreshIcon />, label: "Recargar", onClick: reload },
 *       { key: "expand", icon: <OpenInFullIcon />, label: "Expandir", onClick: toggle },
 *       { key: "close",  icon: <CloseIcon />, label: "Cerrar", onClick: onClose, tone: "danger" },
 *     ]}
 *   />
 *
 * tone por acción: "neutral" (default) · "primary" (teal) · "danger" (cerrar).
 */

const TONE_HOVER = {
  neutral: {
    bgcolor: "rgba(var(--fg-rgb), 0.10)",
    color: "var(--text-secondary)",
  },
  primary: {
    bgcolor: "rgba(var(--color-primary-rgb), 0.20)",
    color: "var(--color-primary-soft)",
  },
  danger: {
    bgcolor: "rgba(var(--color-danger-rgb), 0.16)",
    color: "var(--color-danger)",
  },
};

const ControlCluster = ({ actions = [], size = 34, iconSize = 18, sx }) => {
  const items = actions.filter(Boolean);
  if (!items.length) return null;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        p: "3px",
        flex: "none",
        borderRadius: "999px",
        bgcolor: "rgba(var(--fg-rgb), 0.05)",
        border: "1px solid rgba(var(--fg-rgb), 0.10)",
        backdropFilter: "blur(8px)",
        ...sx,
      }}
    >
      {items.map((a, i) => {
        const hover = TONE_HOVER[a.tone] || TONE_HOVER.neutral;
        const btn = (
          <IconButton
            onClick={a.onClick}
            disabled={a.disabled}
            aria-label={a.label || a.key}
            sx={{
              width: size,
              height: size,
              p: 0,
              color: "var(--text-muted)",
              // El theme tiene un override global de MuiIconButton que rellena
              // TODO IconButton con backgroundColor: color-primary (teal). Hay
              // que anularlo acá o el botón sale azul relleno en vez de ghost.
              backgroundColor: "transparent",
              transition: "all .18s cubic-bezier(0.22,1,0.36,1)",
              "& svg": { fontSize: iconSize },
              "&:hover": hover,
              "&.Mui-disabled": {
                color: "var(--text-fainter)",
                backgroundColor: "transparent",
              },
            }}
          >
            {a.icon}
          </IconButton>
        );
        return (
          <React.Fragment key={a.key || i}>
            {i > 0 && (
              <Box
                sx={{
                  width: "1px",
                  height: 18,
                  flex: "none",
                  bgcolor: "rgba(var(--fg-rgb), 0.10)",
                }}
              />
            )}
            {a.label ? (
              <Tooltip title={a.label} arrow>
                <span style={{ display: "inline-flex" }}>{btn}</span>
              </Tooltip>
            ) : (
              btn
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default ControlCluster;
